/**
 * BuybackFront ↔ parent (Shopify theme) postMessage contract. Parent only accepts
 * messages where e.origin matches https://*.getgreenr.com (see isAllowedOrigin).
 *
 * - { type: 'gg-buyback-embed-height', height: number } — resize iframe (desktop).
 * - { type: 'gg-buyback-embed-scroll', scrollTop: number } — sync iframe scroll for header UI.
 * - { type: 'gg-buyback-embed-parent-scroll-top', behavior?: 'auto' | 'smooth' } —
 *   scroll the storefront window to top (e.g. after SPA step changes inside the iframe).
 * - { type: 'gg-buyback-embed-analytics', payload: object } — mirror of a BuybackFront
 *   dataLayer push (see lib/util/track-event.ts), pushed as-is to the parent's dataLayer.
 */
(function () {
  'use strict';

  var MSG = 'gg-buyback-embed-height';
  var SCROLL_MSG = 'gg-buyback-embed-scroll';
  var PARENT_SCROLL_TOP_MSG = 'gg-buyback-embed-parent-scroll-top';
  var ANALYTICS_MSG = 'gg-buyback-embed-analytics';
  function isAllowedOrigin(origin) {
    if (typeof origin !== 'string') return false;
    // Allow BuybackFront origins across internal navigations (evaluation → offers, etc.)
    // Keep this strict: https only + getgreenr.com subdomains.
    return /^https:\/\/([a-z0-9-]+\.)*getgreenr\.com$/i.test(origin);
  }

  function updateHeaderSearchVisibility(scrollTop) {
    var header = document.querySelector('header[is="sticky-header"]');
    if (!header) return;
    if (!window.matchMedia || !window.matchMedia('(max-width: 1023px)').matches)
      return;

    // Mirror thresholds in assets/header.js
    var SHOW_THRESHOLD_PX = 70;
    var HIDE_THRESHOLD_PX = 90;
    var SCROLL_DELTA_MIN = 14;
    var SEARCH_TOGGLE_COOLDOWN_MS = 400;
    var st = Math.max(0, parseInt(scrollTop, 10) || 0);
    var now = Date.now();

    if (st <= SHOW_THRESHOLD_PX) {
      // Avoid rapid flicker due to iOS overscroll bounce.
      if (
        header._ggLastIframeSearchToggleTime &&
        now - header._ggLastIframeSearchToggleTime < SEARCH_TOGGLE_COOLDOWN_MS
      )
        return;
      header.classList.remove('header--search-hidden');
      header._ggLastIframeScrollTop = st;
      header._ggLastIframeSearchToggleTime = now;
      clearTimeout(header._ggIframeResizeTimer);
      header._ggIframeResizeTimer = setTimeout(setMobileIframeHeight, 320);
      return;
    }
    if (st <= HIDE_THRESHOLD_PX) return;

    var prev =
      typeof header._ggLastIframeScrollTop === 'number'
        ? header._ggLastIframeScrollTop
        : st;
    var delta = st - prev;
    header._ggLastIframeScrollTop = st;

    if (Math.abs(delta) < SCROLL_DELTA_MIN) return;
    if (
      header._ggLastIframeSearchToggleTime &&
      now - header._ggLastIframeSearchToggleTime < SEARCH_TOGGLE_COOLDOWN_MS
    )
      return;

    if (delta > 0) header.classList.add('header--search-hidden');
    else header.classList.remove('header--search-hidden');
    header._ggLastIframeSearchToggleTime = now;

    // The header's hide/show is a CSS transition (transform + #MainContent padding-top), not
    // instant — remeasure once it settles so the iframe's JS-set height/top track the header's
    // final position instead of staying frozen at whatever was measured before it toggled.
    clearTimeout(header._ggIframeResizeTimer);
    header._ggIframeResizeTimer = setTimeout(setMobileIframeHeight, 320);
  }

  function findIframe() {
    return document.querySelector('#MainContent .main-page .page__content iframe');
  }

  function findLoading() {
    return document.querySelector('[data-gg-sell-eval-loading]');
  }

  function setLoading(isLoading) {
    var loading = findLoading();
    if (!loading) return;
    loading.style.display = isLoading ? '' : 'none';
    loading.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function initIframeSrc() {
    var iframe = findIframe();
    if (!iframe) return;
    if (iframe.getAttribute('src')) return;

    var params = new URLSearchParams(window.location.search || '');
    var orderAckHandle = (params.get('order_ack') || '').trim();
    var orderHandle = (params.get('order') || '').trim();
    var slug = (params.get('slug') || '').trim();

    var base, path;
    if (orderAckHandle) {
      base = iframe.getAttribute('data-gg-order-ack-base') || '';
      path = orderAckHandle;
    } else if (orderHandle) {
      base = iframe.getAttribute('data-gg-order-base') || '';
      path = orderHandle;
    } else if (slug) {
      base = iframe.getAttribute('data-gg-eval-base') || '';
      path = slug;
    } else {
      return;
    }
    if (!base) return;
    var embedV = (iframe.getAttribute('data-gg-embed-v') || '').trim();

    setLoading(true);
    iframe.addEventListener(
      'load',
      function () {
        setLoading(false);
        // The very first setMobileIframeHeight() call (script init, before src is
        // set / while loading="lazy" hasn't rendered content yet) can measure a
        // wrong iframeTop/height. Recompute now that the iframe has real content
        // and layout — non-immediate so any correction still animates smoothly
        // instead of snapping, since this isn't the keyboard-open case.
        setMobileIframeHeight();
      },
      { once: true },
    );

    var src = base + encodeURIComponent(path) + '?embed=1';
    // Cache-bust the embedded app when theme code changes.
    if (embedV) src += '&v=' + encodeURIComponent(embedV);
    iframe.setAttribute('src', src);
  }

  function scrollParentToTop(data) {
    var behavior =
      data && data.behavior === 'smooth' ? 'smooth' : 'auto';
    window.scrollTo({ top: 0, left: 0, behavior: behavior });
  }

  window.addEventListener('message', function (e) {
    if (!e.data || typeof e.data.type !== 'string') return;
    if (!isAllowedOrigin(e.origin)) return;

    if (e.data.type === SCROLL_MSG) {
      updateHeaderSearchVisibility(e.data.scrollTop);
      return;
    }

    if (e.data.type === PARENT_SCROLL_TOP_MSG) {
      scrollParentToTop(e.data);
      return;
    }

    if (e.data.type === ANALYTICS_MSG) {
      if (!e.data.payload || typeof e.data.payload !== 'object') return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(e.data.payload);
      return;
    }

    if (e.data.type !== MSG) return;

    var h = parseInt(e.data.height, 10);
    if (!h || h < 320) return;

    var iframe = findIframe();
    if (!iframe) return;

    // When child navigates internally, height messages keep coming. Hide loader if visible.
    setLoading(false);

    // On mobile, keep the iframe as the scroll container so BuybackFront sticky UI works.
    // Height is controlled by CSS (100dvh - header), not by postMessage.
    if (window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches) return;

    iframe.style.height = h + 'px';
    // Keep min-height controlled by theme CSS fallback, not by the last posted height.
    // This allows the iframe to shrink on navigation to shorter routes (e.g. /offers).
    iframe.style.minHeight = '';
    iframe.style.maxHeight = 'none';
  });

  // ── Mobile iframe height ─────────────────────────────────────────────────────
  // On mobile the iframe must be exactly the visible-area height so that
  // BuybackFront's position:fixed bottom:0 CTA lands at the clip boundary and
  // is fully visible.  CSS `100dvh` can be the *large* viewport value (URL bar
  // hidden) while the actual visible area is smaller, pushing the button below
  // the site-wrapper overflow:hidden clip.  window.innerHeight is always the
  // current true visible height, so we set the iframe height directly from JS.
  //
  // `immediate` bypasses the CSS `transition: height/max-height 0.3s` (theme.css)
  // that this same height rule normally animates through for the header
  // hide/show case. That transition is desired there, but for a keyboard
  // open/close (driven by the resize/visualViewport listeners below) the outer
  // iframe box would otherwise animate its shrink over 300ms while
  // BuybackFront's own inner layout (React state) collapses instantly — the two
  // fall out of sync for that window and show up as a visible gap/whitespace.
  function setMobileIframeHeight(immediate) {
    if (!window.matchMedia('(max-width: 767.98px)').matches) return;
    var iframe = findIframe();
    if (!iframe) return;
    // Measure the iframe's actual top — accounts for header + page title + section padding.
    // visualViewport.height is the true visible height on iOS (excludes browser chrome).
    var iframeTop = iframe.getBoundingClientRect().top;
    var viewH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var h = Math.max(100, Math.round(viewH - iframeTop));
    if (immediate) iframe.style.transition = 'none';
    iframe.style.height = h + 'px';
    iframe.style.minHeight = 'unset';
    iframe.style.maxHeight = h + 'px';
    if (immediate) {
      // Force layout so the instant height applies before restoring the
      // transition, otherwise the next (unrelated) height change could pick up
      // the 'none' override and skip its own intended animation.
      // eslint-disable-next-line no-unused-expressions
      iframe.offsetHeight;
      iframe.style.transition = '';
    }
  }

  // Run immediately (non-immediate/transitioned — the iframe has no src yet at
  // this point, so this first measurement can be off; the load-event
  // recompute above corrects it, and letting this one transition avoids
  // freezing a wrong height with no animation to reveal the fix), then keep
  // in sync with viewport/orientation changes.
  setMobileIframeHeight();
  window.addEventListener('resize', function () {
    setMobileIframeHeight(true);
  });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () {
      setMobileIframeHeight(true);
    });
  }
  // ── End mobile iframe height ─────────────────────────────────────────────────

  // Set iframe src based on current page query params.
  // Shopify Liquid isn't guaranteed to expose query params in Online Store renders.
  initIframeSrc();

  // ── Debug overlay ────────────────────────────────────────────────────────────
  // Append ?gg-debug=1 to the URL to show a floating panel with layout metrics.
  // Remove this block (or the URL param) once the issue is diagnosed.
  if (new URLSearchParams(window.location.search).get('gg-debug') === '1') {
    function ggDebugPanel() {
      var iframe = findIframe();
      var r = iframe ? iframe.getBoundingClientRect() : {};
      var hVar = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-group-height').trim() || '(not set)';
      var hVarH = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height').trim() || '(not set)';
      var lines = [
        'window: ' + window.innerWidth + 'x' + window.innerHeight,
        '--header-group-height: ' + hVar,
        '--header-height: ' + hVarH,
        'iframe rect: t=' + Math.round(r.top) + ' h=' + Math.round(r.height),
        'iframe bottom: ' + Math.round(r.bottom),
        'overflow: ' + (r.bottom > window.innerHeight ? 'CLIPPED by ' + Math.round(r.bottom - window.innerHeight) + 'px' : 'ok'),
      ];
      return lines.join('\n');
    }
    var dbg = document.createElement('div');
    dbg.id = 'gg-debug-panel';
    dbg.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:99999',
      'background:rgba(0,0,0,0.85)', 'color:#0f0', 'font:12px/1.5 monospace',
      'white-space:pre', 'padding:8px', 'pointer-events:none',
    ].join(';');
    document.body.appendChild(dbg);
    function ggUpdateDebug() { dbg.textContent = ggDebugPanel(); }
    ggUpdateDebug();
    setInterval(ggUpdateDebug, 500);
  }
  // ── End debug overlay ────────────────────────────────────────────────────────
})();
